/**
 * Canonical SyNeural terminology and shared copy.
 * Headers, footers, cards and pages read from here so names and status
 * language cannot drift apart.
 */

export type ComponentId =
  | 'syneural'
  | 'skp'
  | 'hcf'
  | 'cji'
  | 'ncji'
  | 'noetivra'
  | 'ncds'
  | 'cognocracy'
  | 'cognarchism'
  | 'cognarchicState'

export type ComponentDefinition = {
  id: ComponentId
  name: string
  shortName?: string
  definition: string
  href?: string
}

export const componentDefinitions: Record<ComponentId, ComponentDefinition> = {
  syneural: {
    id: 'syneural',
    name: 'SyNeural',
    definition:
      'Develops connected knowledge infrastructure, human capability frameworks and interactive workspaces for putting knowledge into action.',
    href: '/about',
  },
  skp: {
    id: 'skp',
    name: 'Semantic Knowledge Platform',
    shortName: 'SKP',
    definition:
      'Connects and governs knowledge, sources, concepts, relationships and review history. AI-assisted proposals remain distinct from approved knowledge.',
    href: '/semantic-knowledge',
  },
  hcf: {
    id: 'hcf',
    name: 'Human Capability Framework',
    shortName: 'HCF',
    definition:
      'Structures assessment, human review, development and reassessment. It is the umbrella for CJI and NCJI methods and development pathways.',
    href: '/human-capability',
  },
  cji: {
    id: 'cji',
    name: 'Civic Judgment Index',
    shortName: 'CJI',
    definition:
      'Defines structured reasoning and judgment assessment methods within HCF, including tasks, criteria and interpretation guidance.',
    href: '/cji',
  },
  ncji: {
    id: 'ncji',
    name: 'Neurocognitive Civic Judgment Index',
    shortName: 'NCJI',
    definition:
      'Organizes civic judgment assessment and development around five pillars within HCF, connecting findings with support, practice and reassessment.',
    href: '/ncji',
  },
  noetivra: {
    id: 'noetivra',
    name: 'Noetivra',
    definition:
      'Provides domain-specific interactive workspaces for exploring knowledge, reviewing evidence, coordinating work and following development.',
    href: '/noetivra',
  },
  ncds: {
    id: 'ncds',
    name: 'National Capability and Development System',
    shortName: 'NCDS',
    definition:
      'A proposed government implementation connecting approved knowledge, appropriate assessments, development support, public services and program evaluation.',
    href: '/government',
  },
  cognocracy: {
    id: 'cognocracy',
    name: 'Cognocracy',
    definition: 'A civic governance concept proposal drawing on SyNeural’s architecture.',
    href: '/projects/cognocracy',
  },
  cognarchism: {
    id: 'cognarchism',
    name: 'Cognarchism',
    definition:
      'The related philosophy. It is not a requirement for adopting SyNeural’s commercial products.',
  },
  cognarchicState: {
    id: 'cognarchicState',
    name: 'Cognarchic State',
    definition: 'The canonical name of the related state concept.',
  },
}

/** Relationship paragraph used on HCF, CJI and NCJI. */
export const frameworkRelationship =
  'HCF provides the overall assessment and development framework. CJI defines structured reasoning and judgment assessment methods. NCJI organizes civic judgment assessment and development around five pillars. SKP maintains their supporting knowledge and versions, while Noetivra provides the interactive workspaces.'

/** Replaces generic Noetivra "Coming soon" labels. */
export const noetivraAvailabilityLabel = 'Interactive demos available · Production platform in development'

/** Shared availability paragraph for Noetivra, Demo Center and solution pages. */
export const demoAvailability =
  'Explore seven interactive demonstrations using fictional records. The demos illustrate workflows and save changes in your browser. Production integrations, secure institutional accounts and validated assessment methods are separate development milestones.'

/** Assessment research status, used wherever assessment is described. */
export const assessmentResearchStatus =
  'The five-pillar architecture is our working model. Assessment instruments remain under development. Pilot studies are needed to establish reliability, validity, lasting improvement and transfer to real-world decisions.'

/** Replaces installation-first descriptions. */
export const deliveryFormat =
  'The current demonstrations run in a web browser. Production deployment options, integrations and any installable applications remain part of product development.'

/** Shared capabilities across domains (not extra domains). */
export const sharedCapabilitiesCopy =
  'Education, Talent, Leadership and Community demonstrations reuse a common development workflow, adapted to their participants and responsibilities. Leadership exercises can also be configured within employer, government, education and community programs. Collaboration connects authorized contributions and reviews across domains. Research and Clinical can use SKP without requiring CJI or NCJI assessment.'

/** Note placed beneath CJI's six domains. */
export const cjiScopeNote =
  'These describe CJI’s assessment scope. NCJI uses a separate five-pillar working architecture to organize civic judgment and development. The two descriptions overlap, but they are not interchangeable lists. Their task-level relationships will be specified and tested as the assessment methods develop.'

/** Explains how the HCF cycle and the NCJI pathway relate. */
export const cycleRelationship =
  'In the HCF cycle, Review includes explaining and checking findings, and Develop includes recommendations, practice and support. Advance, in the NCJI pathway, means moving toward another development goal when the evidence supports it.'

/** Aggregate views never describe individuals. */
export const aggregateNote =
  'Group patterns describe a population or institution. They do not establish any individual’s capabilities.'

/** Institutional outcomes are not attributed solely to individual judgment. */
export const institutionalConditionsNote =
  'Institutional outcomes also depend on resources, incentives, services and conditions. They are not attributed solely to individual judgment.'

/**
 * Proposed future uses, presented as possibilities the architecture could
 * support. None of these exist in the current demonstrations.
 */
export type FutureUseArea = 'hcf' | 'leadership' | 'government' | 'talent' | 'clinical'

export type FutureUseGroup = {
  title: string
  uses: string[]
  conditions: string
}

export const futureUseConditions =
  'None of these are part of the current demonstrations. Each would require validation for its specific purpose, legal and regulatory review in the jurisdiction where it is used, independent oversight and meaningful human review and appeal.'

export const futureUses: Record<FutureUseArea, FutureUseGroup> = {
  hcf: {
    title: 'Possible future uses of the framework',
    uses: [
      'An overall intelligence score summarizing several assessed areas',
      'A civic rank or eligibility rating derived from assessment results',
    ],
    conditions:
      'Today the framework presents a five-pillar profile with no overall score, rank or rating. Any such summary would need evidence that it is valid for its intended use.',
  },
  leadership: {
    title: 'Possible future uses in leadership development',
    uses: ['A universal leadership certification recognized across programs and organizations'],
    conditions:
      'The current demo supports coached practice and reflection only. Certification would need role-specific validation and an accountable issuing body.',
  },
  government: {
    title: 'Possible future uses in government',
    uses: [
      'A citizen-management panel for public administrators',
      'A civic rank or automatic eligibility determination for public services or responsibilities',
    ],
    conditions:
      'The current demo keeps service decisions separate from assessment findings and with human reviewers. Automatic eligibility would raise substantial questions about equal participation, due process and appeals.',
  },
  talent: {
    title: 'Possible future uses in hiring and workforce',
    uses: ['Automatic candidate ranking', 'Inferred-emotion scoring'],
    conditions:
      'The current demo does not rank candidates and does not use physiological measurements or inferred emotions; hiring decisions remain with human reviewers. Emotion inference in workplaces is restricted or prohibited in some jurisdictions.',
  },
  clinical: {
    title: 'Possible future uses in clinical settings',
    uses: [
      'Clinical assessment support',
      'Diagnosis support',
      'Treatment recommendations',
      'Physiological inference from sensor data',
      'Civic judgment assessment alongside clinical review',
    ],
    conditions:
      'The current demo reviews documentation only and does not diagnose or recommend treatment. These uses would require clinical validation, applicable medical-device approval and clinicians retaining responsibility for every decision.',
  },
}
